// @flow

import Promise from 'bluebird';
import * as React from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Button, Footer } from '@sonyged/ext-proc';

import { type FinishedRound, Round } from './domain/Round';
import { PizzaStaff } from './PizzaStaff';
import { ScaleContext } from './ScaleContext';
import { Stars } from './Stars';
import { StoreTalkBubbleArrow } from './StoreTalkBubble';
import AppContext from '../../../AppContext';
import AutoScaleContainer from './AutoScaleContainer';
import PizzaCustomer from './PizzaCustomer';
import PizzaDish from './PizzaDish';
import PizzaStore from './PizzaStore';
import PizzaTable from './PizzaTable';

type Props = {
  round: FinishedRound,
  onNext: FinishedRound => void,
};

type State = {
  talkingCustomer: number | null,
  staffLine: ?string,
  animateCustomer: boolean,
  showResult: boolean,
  showPizza: boolean,
  showStaff: boolean,
  status: 'before' | 'eating' | 'after',
  scale: number,
};

class PizzaEatingView extends React.Component<Props, State> {
  static contextType = AppContext;

  mogumoguTimerId: IntervalID | void;

  constructor(props: Props) {
    super(props);
    this.state = {
      talkingCustomer: null,
      staffLine: 'おまたせしました。',
      animateCustomer: false,
      showResult: false,
      showPizza: false,
      showStaff: true,
      status: 'before',
      scale: 1,
    };

    (this: any).handleClickNext = this.handleClickNext.bind(this);
    (this: any).stopAnimateCustomer = this.stopAnimateCustomer.bind(this);
    (this: any).showRating = this.showRating.bind(this);
    (this: any).handleScaleChange = this.handleScaleChange.bind(this);
  }

  componentDidMount() {
    Promise.delay(800)
      .then(() => this.hideStaff())
      .delay(500)
      .then(() => this.showPizza())
      .delay(500)
      .then(() => this.animateCustomer())
      .delay(3500)
      .then(() => this.showResult());
  }

  handleClickNext() {
    if (this.state.status !== 'after') return;
    this.props.onNext(this.props.round);
  }

  handleScaleChange(scale: number) {
    this.setState({ scale });
  }

  showPizza(): Promise<void> {
    return new Promise(resolve => {
      if (this.state.showPizza) return resolve();
      this.setState({ showPizza: true }, resolve);
    });
  }

  hideStaff(): Promise<void> {
    return new Promise(resolve => {
      if (!this.state.showStaff) return resolve();
      this.setState({ showStaff: false }, resolve);
    });
  }

  animateCustomer(): Promise<void> {
    return new Promise(resolve => {
      if (this.state.animateCustomer) return resolve();
      this.setState({ animateCustomer: true }, resolve);
    });
  }

  showResult(): Promise<void> {
    return new Promise(resolve => {
      if (this.state.showResult) return resolve();
      this.context.soundController.sePlay('stamp');
      this.setState({ showResult: true }, resolve);
    });
  }

  stopAnimateCustomer() {
    if (!this.state.animateCustomer) return;
    this.mogumoguTimerId = setInterval(() => {
      this.context.soundController.sePlay('mogumogu2');
    }, 500);
    this.context.soundController.sePlay('mogumogu2');
    this.setState({ animateCustomer: false, status: 'eating' });
  }

  showRating() {
    if (this.mogumoguTimerId) {
      clearInterval(this.mogumoguTimerId);
    }
    setTimeout(() => {
      this.context.soundController.sePlay('star');
    }, 1000);
    this.setState({ status: 'after' });
  }

  render() {
    let pizzaSize = 'normal';
    if (this.props.round.customers.length === 2) pizzaSize = 'medium';
    if (this.props.round.customers.length === 3) pizzaSize = 'large';

    const customerRatings = Round.ratings(this.props.round);

    return (
      <div className="pizza-inner-view pizza-eating-view">
        <div className={'proc-contents padding relative'}>
          <AutoScaleContainer
            className="tertiary100-bg-color"
            preferedWidth={864}
            preferedHeight={474}
            preferedMinHeight={474}
            preferedMinWidth={360}
            onScaleChange={this.handleScaleChange}
          >
            <PizzaStore>
              {customerRatings[0] && (
                <PizzaCustomer
                  className={`pizza-customer-rating ${this.state.animateCustomer ? 'animate' : ''}`}
                  talking={this.state.status === 'after'}
                  customer={customerRatings[0].customer}
                  position={customerRatings.length === 3 ? 'left' : 'center'}
                  onAnimationEnd={this.stopAnimateCustomer}
                >
                  <div className="pizza-storeRatingBubble">
                    <Stars satisfactionScore={customerRatings[0].rating} />
                  </div>
                  <StoreTalkBubbleArrow />
                </PizzaCustomer>
              )}
              {customerRatings[1] && (
                <PizzaCustomer
                  className={`pizza-customer-rating ${this.state.animateCustomer ? 'animate' : ''}`}
                  talking={this.state.status === 'after'}
                  customer={customerRatings[1].customer}
                  position={customerRatings.length === 3 ? 'center' : 'right'}
                >
                  <div className="pizza-storeRatingBubble">
                    <Stars satisfactionScore={customerRatings[1].rating} />
                  </div>
                  <StoreTalkBubbleArrow />
                </PizzaCustomer>
              )}
              {customerRatings[2] && (
                <PizzaCustomer
                  className={`pizza-customer-rating ${this.state.animateCustomer ? 'animate' : ''}`}
                  talking={this.state.status === 'after'}
                  customer={customerRatings[2].customer}
                  position="right"
                >
                  <div className="pizza-storeRatingBubble">
                    <Stars satisfactionScore={customerRatings[2].rating} />
                  </div>
                  <StoreTalkBubbleArrow />
                </PizzaCustomer>
              )}
              <PizzaTable />
              <TransitionGroup>
                {this.state.showPizza && (
                  <CSSTransition classNames="slidein" timeout={200} key="pizza-dish">
                    <PizzaDish pizza={this.props.round.pizza} size={pizzaSize} ate={this.state.status === 'after'} />
                  </CSSTransition>
                )}
              </TransitionGroup>
            </PizzaStore>
            {this.state.showResult && (
              <ScaleContext.Consumer>
                {scale => {
                  const containerStyle = {
                    maxWidth: scale * 368,
                    height: scale * 160,
                  };

                  const stampStyle = {
                    bottom: 10 * scale,
                    right: 10 * scale,
                    width: 82 * scale,
                    height: 82 * scale,
                  };

                  const smallFontSize = {
                    fontSize: 15 * scale,
                  };

                  const amountFontSize = {
                    fontSize: 50 * scale,
                  };

                  const yenFontSize = {
                    fontSize: 32 * scale,
                  };

                  const ratings = Round.ratings(this.props.round).map(r => r.rating);
                  const avgRatings = ratings.reduce((x, y) => x + y) / ratings.length;
                  let grade = 3;
                  if (avgRatings > 3) grade = 1;
                  else if (avgRatings > 0) grade = 2;

                  return (
                    <div
                      style={containerStyle}
                      className={'pizza-price-container proc-border-radius secondary20-border-color center-aligned'}
                    >
                      <div className={'background absolute white-bg-color'} />
                      <div className={'content absolute'}>
                        <div style={smallFontSize} className={'title secondary100-color'}>
                          もらった お金{' '}
                        </div>
                        <div style={amountFontSize} className={'gray100-color'}>
                          {Round.sale(this.props.round)}
                          <span style={yenFontSize}> 円</span>
                        </div>
                        <div style={stampStyle} className={`pizza-stamp pizza-stamp--${grade}`} />
                      </div>
                    </div>
                  );
                }}
              </ScaleContext.Consumer>
            )}
          </AutoScaleContainer>
          <PizzaStaff scale={this.state.scale} message={this.state.staffLine} showing={this.state.showStaff} />
        </div>
        <Footer.normal>
          <Button.attention disabled={this.state.status !== 'after'} onClick={this.handleClickNext}>
            つぎへ
          </Button.attention>
        </Footer.normal>
        {this.state.status === 'eating' && (
          <div className="eating-modal tertiary100-bg-color">
            <div className="pizza-eating-sprite" onAnimationEnd={this.showRating} />
          </div>
        )}
      </div>
    );
  }
}

export default PizzaEatingView;
